/** TypeScript types for flima_rebirth frontend. */

// --- Config types (mirror backend config schema) ---

export interface ContactConfig {
  name: string;
  email: string;
  referat: string;
}

export interface BrandingConfig {
  title: string;
  subtitle: string;
  logo: string;
  partner_logos: string[];
  contact: ContactConfig;
  data_stamp: string;
}

export interface UiTextConfig {
  language: string;
  de: Record<string, string>;
  en: Record<string, string>;
}

export interface BaseLayerConfig {
  file: string;
  color: string;
  opacity: number;
}

export interface SpeciesConfig {
  id: string;
  name: string;
  latin: string;
  color: string;
  habitat_file: string;
  image: string;
  info_url: string;
}

export interface MeasureConfig {
  id: string;
  label: string;
  label_with_dates: string;
  species: string;
  attribute: string;
  months: number[];
  color: string;
}

export interface OverlapColors {
  both: string;
  bach_only: string;
  helm_only: string;
  none: string;
}

export interface RegionConfig {
  region: { name: string; bbox: [number, number, number, number] };
  species: SpeciesConfig[];
  measures: MeasureConfig[];
  overlap_colors: OverlapColors;
  base_layers: Record<string, BaseLayerConfig>;
  branding: BrandingConfig;
  ui_text: UiTextConfig;
}

// --- GeoJSON types ---

export type GeoJSONPosition = number[];

export type GeoJSONGeometry =
  | { type: "Point"; coordinates: GeoJSONPosition }
  | { type: "MultiPoint" | "LineString"; coordinates: GeoJSONPosition[] }
  | { type: "MultiLineString" | "Polygon"; coordinates: GeoJSONPosition[][] }
  | { type: "MultiPolygon"; coordinates: GeoJSONPosition[][][] }
  | { type: "GeometryCollection"; geometries: GeoJSONGeometry[] };

export interface GeoJSONFeature {
  type: "Feature";
  geometry: GeoJSONGeometry;
  properties: Record<string, unknown>;
}

export interface GeoJSONFeatureCollection {
  type: "FeatureCollection";
  features: GeoJSONFeature[];
}

/** A municipality search option. `uid` is the stable identity sent to the API
 * (names are not unique once the region expands); `name` is for display and
 * `region` is an admin descriptor shown only to disambiguate colliding names. */
export interface GemeindeOption {
  uid: string;
  name: string;
  region: string;
}

// --- Catalogue ---

export interface CatalogueEntry {
  measure_id: string;
  label: string;
  label_with_dates: string;
  species: string;
  description: string;
  description_en: string;
  details: string;
  details_en: string;
  image_path: string;
  months: number[];
  color: string;
}

// --- Filter state ---

export interface FilterState {
  species: Set<string>;
  months: Set<number>;
  measures: Set<string>;
}

// --- Layer visibility ---

export type LayerVisibility = Record<string, boolean>;

export type MapMode = "habitats" | "planning";

// --- API error ---

export class ApiError extends Error {
  status: number;
  detail: string;

  constructor(status: number, detail: string) {
    super(detail);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}
